import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  GlobalStyles,
  Paper,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle,
  Business,
  AttachMoney,
} from '@mui/icons-material';
import { FeePreview } from './FeePreview';

// Aggressive CSS overrides to defeat Bulma - KEEPS BORDERS INTACT
const bulmaOverrides = {
  '.fi-config-modal-wrapper': {
    // Reset ALL Bulma input styling
    '& input, & select, & textarea': {
      boxShadow: 'none !important',
      border: 'none !important',
      padding: '0 !important',
      height: 'auto !important',
      lineHeight: 'inherit !important',
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
      fontSize: 'inherit !important',
      fontFamily: 'inherit !important',
      width: 'auto !important',
      maxWidth: 'none !important',
      margin: '0 !important',
      borderRadius: '0 !important',
      '&:focus, &:active, &:hover': {
        border: 'none !important',
        boxShadow: 'none !important',
        outline: 'none !important',
      },
    },
    // CRITICAL: Force MUI TextField borders to show - DO NOT REMOVE
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff !important',
      borderRadius: '8px !important',
      transition: 'all 0.2s ease-in-out !important',
      overflow: 'visible !important',
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23) !important',
        borderWidth: '1px !important',
        borderStyle: 'solid !important',
        borderRadius: '8px !important',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.5) !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2563eb !important',
        borderWidth: '2px !important',
      },
      '&.Mui-error fieldset': {
        borderColor: '#dc2626 !important',
      },
      '& input': {
        textOverflow: 'clip !important',
      },
      '& textarea': {
        textOverflow: 'clip !important',
      },
    },
    '& .MuiInputBase-input': {
      padding: '14px 16px !important',
      fontSize: '15px !important',
      lineHeight: '1.5 !important',
      color: '#1f2937 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      fontWeight: '400 !important',
    },
    '& .MuiInputLabel-root': {
      fontSize: '15px !important',
      color: '#6b7280 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      fontWeight: '500 !important',
      '&.Mui-focused': {
        color: '#2563eb !important',
        fontWeight: '600 !important',
      },
      '&.Mui-error': {
        color: '#dc2626 !important',
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '13px !important',
      margin: '6px 14px 0 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      lineHeight: '1.4 !important',
    },
    // Dialog styling
    '& .MuiDialog-paper': {
      maxWidth: '650px !important',
      width: '100% !important',
      borderRadius: '12px !important',
    },
    '& .MuiDialogTitle-root': {
      padding: '28px 32px 20px !important',
      fontSize: '24px !important',
      fontWeight: '700 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#111827 !important',
      background: 'linear-gradient(to bottom, #f9fafb, #ffffff) !important',
      borderBottom: '1px solid #e5e7eb !important',
    },
    '& .MuiDialogContent-root': {
      padding: '24px 32px !important',
      backgroundColor: '#f9fafb !important',
    },
    '& .MuiDialogActions-root': {
      padding: '20px 32px 24px !important',
      backgroundColor: '#ffffff !important',
      borderTop: '1px solid #e5e7eb !important',
      gap: '12px !important',
    },
    // Typography
    '& .MuiTypography-h6': {
      fontSize: '18px !important',
      fontWeight: '700 !important',
      margin: '0 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#111827 !important',
    },
    // Buttons
    '& .MuiButton-root': {
      textTransform: 'none !important',
      fontSize: '15px !important',
      fontWeight: '600 !important',
      padding: '10px 24px !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      borderRadius: '8px !important',
      minHeight: '40px !important',
      '&.MuiButton-contained': {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06) !important',
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important',
        },
      },
    },
    // Switch styling
    '& .MuiSwitch-root': {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#2563eb !important',
        '& + .MuiSwitch-track': {
          backgroundColor: '#2563eb !important',
        },
      },
    },
    '& .MuiFormControlLabel-label': {
      fontSize: '15px !important',
      fontWeight: '500 !important',
      color: '#374151 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
    },
  },
};

const initialFormState = {
  allows_payments: false,
  reseller_split_percentage: '60.00',
  solvent_buy_rate_percentage: '1.00',
  solvent_buy_rate_usd: '0.25',
};

export function FIConfigModal({
  open,
  onClose,
  onSave,
  fiData,
}) {
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (open && fiData) {
      setFormData({
        allows_payments: fiData.allows_payments ?? false,
        reseller_split_percentage: fiData.reseller_split_percentage || '60.00',
        solvent_buy_rate_percentage: fiData.solvent_buy_rate_percentage || '1.00',
        solvent_buy_rate_usd: fiData.solvent_buy_rate_usd || '0.25',
      });
      setError(null);
      setValidationErrors({});
    }
  }, [open, fiData]);

  const validateField = (field, value) => {
    const errors = {};

    if (field === 'reseller_split_percentage') {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0 || num > 100) {
        errors.reseller_split_percentage = 'Must be between 0 and 100';
      }
    }

    if (field === 'solvent_buy_rate_percentage') {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0 || num > 100) {
        errors.solvent_buy_rate_percentage = 'Must be between 0 and 100';
      }
    }

    if (field === 'solvent_buy_rate_usd') {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        errors.solvent_buy_rate_usd = 'Must be 0 or greater';
      }
    }

    return errors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Real-time validation for percentage fields
    if (field !== 'allows_payments') {
      const errors = validateField(field, value);
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        if (Object.keys(errors).length > 0) {
          Object.assign(newErrors, errors);
        } else {
          delete newErrors[field];
        }
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    // Validate all fields
    const allErrors = {};
    Object.entries(formData).forEach(([field, value]) => {
      const fieldErrors = validateField(field, value);
      Object.assign(allErrors, fieldErrors);
    });

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      setError('Please fix the validation errors before submitting');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save FI configuration');
    } finally {
      setSaving(false);
    }
  };

  // Memoized preview values
  const previewParams = useMemo(() => ({
    solventBuyRatePct: parseFloat(formData.solvent_buy_rate_percentage) || 0,
    solventBuyRateUsd: parseFloat(formData.solvent_buy_rate_usd) || 0,
    resellerSplitPct: parseFloat(formData.reseller_split_percentage) || 0,
  }), [formData.solvent_buy_rate_percentage, formData.solvent_buy_rate_usd, formData.reseller_split_percentage]);

  return (
    <>
      <GlobalStyles styles={bulmaOverrides} />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        className="fi-config-modal-wrapper"
      >
        <DialogTitle>
          ISO Payment Configuration
          {fiData && (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 500, color: '#6b7280' }}>
              {fiData.name} {fiData.dba_name && `• DBA: ${fiData.dba_name}`}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                mb: 3,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {error}
            </Alert>
          )}

          {/* FI Information (Read-only) */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <Business sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">ISO Information</Typography>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  ISO Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {fiData?.name || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  DBA Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {fiData?.dba_name || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Payment Settings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <AttachMoney sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Payment Settings</Typography>
            </div>

            {/* Payment Toggle */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: formData.allows_payments ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${formData.allows_payments ? '#bbf7d0' : '#fecaca'}`,
                borderRadius: '8px',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allows_payments}
                    onChange={handleChange('allows_payments')}
                  />
                }
                label={
                  <div>
                    <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>
                      Enable PYUSD Payment Processing
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
                      {formData.allows_payments
                        ? 'This ISO can offer PYUSD payment processing to merchants'
                        : 'Payment processing is disabled for this ISO'}
                    </Typography>
                  </div>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>

            <Alert severity="info" sx={{ mb: 3, fontSize: '13px', borderRadius: '8px' }}>
              <strong>Share-of-Net Revenue Model:</strong><br/>
              • <strong>Solvent Buy Rate</strong>: Solvent's guaranteed minimum per transaction<br/>
              • <strong>Markup</strong> = Total Fees - Solvent Buy Rate<br/>
              • <strong>ISO gets</strong>: Reseller Split % of the markup
            </Alert>

            {/* Solvent Buy Rate Section */}
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 1.5 }}>
              Solvent Buy Rate (Solvent's Guaranteed Minimum)
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Solvent Buy Rate %"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  value={formData.solvent_buy_rate_percentage}
                  onChange={handleChange('solvent_buy_rate_percentage')}
                  error={!!validationErrors.solvent_buy_rate_percentage}
                  helperText={validationErrors.solvent_buy_rate_percentage || 'Percentage of transaction amount'}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Solvent Buy Rate Flat"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={formData.solvent_buy_rate_usd}
                  onChange={handleChange('solvent_buy_rate_usd')}
                  error={!!validationErrors.solvent_buy_rate_usd}
                  helperText={validationErrors.solvent_buy_rate_usd || 'Flat USD added per transaction'}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Reseller Split Section */}
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 1.5 }}>
              Reseller Split (ISO Share of Markup)
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reseller Split %"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  value={formData.reseller_split_percentage}
                  onChange={handleChange('reseller_split_percentage')}
                  error={!!validationErrors.reseller_split_percentage}
                  helperText={validationErrors.reseller_split_percentage || 'ISO\'s percentage share of markup above Solvent\'s buy rate'}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Live Fee Preview */}
            <FeePreview
              solventBuyRatePct={previewParams.solventBuyRatePct}
              solventBuyRateUsd={previewParams.solventBuyRateUsd}
              resellerSplitPct={previewParams.resellerSplitPct}
              transactionAmount={100}
            />
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6',
                color: '#111827',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <CheckCircle />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              },
              '&:disabled': {
                background: '#e5e7eb',
                color: '#9ca3af',
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
